import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ReferenceLine } from 'recharts';
import { useMemo } from 'react';

export default function StressPerformanceCorrelation({ individualTrends }) {
  const scatterData = useMemo(() => {
    if (!individualTrends || individualTrends.length === 0) return [];
    return individualTrends
      .filter(d => d.stress > 0 && d.performance_rating > 0)
      .map(d => ({
        stress: d.stress,
        performance: d.performance_rating,
        date: d.date,
        mood: d.mood
      }));
  }, [individualTrends]);

  const correlation = useMemo(() => {
    if (scatterData.length < 3) return null;
    const n = scatterData.length;
    const sumX = scatterData.reduce((s, d) => s + d.stress, 0);
    const sumY = scatterData.reduce((s, d) => s + d.performance, 0);
    const sumXY = scatterData.reduce((s, d) => s + d.stress * d.performance, 0);
    const sumX2 = scatterData.reduce((s, d) => s + d.stress * d.stress, 0);
    const sumY2 = scatterData.reduce((s, d) => s + d.performance * d.performance, 0);
    const num = n * sumXY - sumX * sumY;
    const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    return den === 0 ? 0 : (num / den);
  }, [scatterData]);

  if (scatterData.length < 3) return null;

  const avgStress = +(scatterData.reduce((s, d) => s + d.stress, 0) / scatterData.length).toFixed(1);
  const avgPerf = +(scatterData.reduce((s, d) => s + d.performance, 0) / scatterData.length).toFixed(1);

  return (
    <div data-testid="stress-performance-chart" className="bg-white rounded-xl shadow-lg p-6 border border-emerald-100">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-gray-900">Stress vs Performance</h2>
        {correlation !== null && (
          <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
            correlation < -0.3 ? 'bg-green-50 text-green-700' : correlation > 0.3 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'
          }`}>
            r = {correlation.toFixed(2)} ({correlation < -0.3 ? 'Higher stress hurts performance' : correlation > 0.3 ? 'Stress may help you peak' : 'Weak relationship'})
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">Each dot is one day. See how your stress level relates to your performance.</p>
      <ResponsiveContainer width="100%" height={280}>
        <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" dataKey="stress" name="Stress" domain={[0, 10]} tick={{ fontSize: 10 }}
            label={{ value: 'Stress Level', position: 'insideBottom', offset: -5, fontSize: 11 }} />
          <YAxis type="number" dataKey="performance" name="Performance" domain={[0, 10]} tick={{ fontSize: 10 }}
            label={{ value: 'Performance', angle: -90, position: 'insideLeft', fontSize: 11 }} />
          <ZAxis dataKey="mood" range={[40, 160]} name="Mood" />
          <ReferenceLine x={avgStress} stroke="#9ca3af" strokeDasharray="4 4" />
          <ReferenceLine y={avgPerf} stroke="#9ca3af" strokeDasharray="4 4" />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div className="bg-white rounded-lg shadow-lg border px-3 py-2 text-xs">
                <p className="font-bold">{d.date}</p>
                <p>Stress: {d.stress}/10</p>
                <p>Performance: {d.performance}/10</p>
                <p>Mood: {d.mood}/10</p>
              </div>
            );
          }} />
          <Scatter data={scatterData} fill="#10b981" fillOpacity={0.6} strokeWidth={1} stroke="#059669" />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
